import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Description, Folder, Science } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getJobFiles } from '@/api/jobs';
import { ModelType } from '@/types/colabfold';
import { useLocation } from 'react-router-dom';

interface RFDiffusionResultsType {
  params?: {
    num_designs?: number;
    contigs?: string;
    symmetry?: {
      type?: string;
      order?: number;
    };
    input_pdb?: string;
    noise_scale?: number;
    temperature?: number;
    query_sequence?: string;
    num_diffusion_steps?: number;
    seed?: number;
    motif_scaffold?: string;
  };
  files?: string[];
  sequence?: string;
  seed?: number;
  length?: number;
  contigs?: string;
  symmetry?: {
    type?: string;
    order?: number;
  };
  num_designs?: number;
  noise_scale?: number;
  temperature?: number;
  num_diffusion_steps?: number;
  query_sequence?: string;
}

interface RFDiffusionResultsProps {
  results: RFDiffusionResultsType;
  jobId: string;
}

interface DesignType {
  designNumber: number | string;
  files: string[];
}

export const RFDiffusionResults: React.FC<RFDiffusionResultsProps> = ({ results, jobId }) => {
  const location = useLocation();
  // Use the files API to get the actual files
  const { data: filesData, isLoading: filesLoading, error: filesError } = useQuery({
    queryKey: ['jobFiles', jobId, ModelType.RFDIFFUSION],
    queryFn: () => getJobFiles(ModelType.RFDIFFUSION, jobId),
  });
  
  // Extract parameters from results
  const params = results?.params || {};
  const numDesigns = params.num_designs || results?.num_designs || 1;
  const contigs = params.contigs || results?.contigs || 'N/A';
  const symmetry = params.symmetry || results?.symmetry || {};
  const symmetryType = symmetry.type || 'None';
  const symmetryOrder = symmetry.order || 'N/A';
  const noiseScale = params.noise_scale || results?.noise_scale || 'N/A';
  const temperature = params.temperature || results?.temperature || 'N/A';
  const numSteps = params.num_diffusion_steps || results?.num_diffusion_steps || 'N/A';
  const seed = params.seed || results?.seed || 'N/A';
  const querySequence = params.query_sequence || results?.query_sequence || '';
  
  // Process files data to organize by design
  const [designs, setDesigns] = useState<DesignType[]>([]);
  // Add a state for the selected design for viewing in 3D
  const [selectedDesign, setSelectedDesign] = useState<string | null>(null);
  
  // Add console logs to debug the data
  useEffect(() => {
    console.log("RFDiffusion Results:", results);
    console.log("Files Data:", filesData);
  }, [results, filesData]);
  
  // Parse query parameters to get the selected file
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const file = searchParams.get('file');
    if (file) {
      setSelectedDesign(file);
      
      // Extract design number from filename (e.g., output_0.pdb -> 0)
      const match = file.match(/output_(\d+)/);
      if (match) {
        const designNum = match[1];
        // Update document title to reflect the currently viewed design
        document.title = `Design ${designNum} - RFDiffusion Results`;
      }
    }
  }, [location.search]);
  
  useEffect(() => {
    if (filesData?.files) {
      console.log("Processing files:", filesData.files);
      const outputFiles = filesData.files.filter((file: string) => 
        !file.startsWith('logs/') && file !== 'status.txt'
      );
      console.log("Filtered output files:", outputFiles);
      
      // Group files by design number
      const designMap = new Map<number | string, DesignType>();
      
      // First, process all regular PDB files to establish design entries
      outputFiles.forEach((file: string) => {
        if (!file.includes('_traj') && file.endsWith('.pdb')) {
          const match = file.match(/output_(\d+)/);
          if (match) {
            const designNum = parseInt(match[1]);
            if (!designMap.has(designNum)) {
              designMap.set(designNum, {
                designNumber: designNum,
                files: []
              });
            }
            designMap.get(designNum)!.files.push(file);
          }
        }
      });
      
      // Then process all other files and assign them to the appropriate design
      outputFiles.forEach((file: string) => {
        if (file === 'traj/') {
          // Handle trajectory directory
          if (!designMap.has('traj')) {
            designMap.set('traj', {
              designNumber: 'traj',
              files: ['traj/']
            });
          }
        } else if (file.includes('_traj') || file.endsWith('.trb')) {
          // This is a trajectory file or metadata file for a specific design
          const match = file.match(/output_(\d+)/);
          if (match) {
            const designNum = parseInt(match[1]);
            if (!designMap.has(designNum)) {
              // Create design entry if it doesn't exist yet
              designMap.set(designNum, {
                designNumber: designNum,
                files: []
              });
            }
            designMap.get(designNum)!.files.push(file);
          }
        }
      });
      
      // If no designs were detected but we have output files, create a default design entry
      if (designMap.size === 0 && outputFiles.length > 0) {
        // Create a design entry for design 0
        designMap.set(0, {
          designNumber: 0,
          files: outputFiles.filter((f: string) => !f.startsWith('traj/'))
        });
      }
      
      // Convert map to array and sort by design number
      const designsArray = Array.from(designMap.values())
        .filter(design => design.designNumber !== 'traj')
        .sort((a, b) => {
          if (typeof a.designNumber === 'number' && typeof b.designNumber === 'number') {
            return a.designNumber - b.designNumber;
          }
          return 0;
        });
      
      // Add trajectory at the end if it exists
      if (designMap.has('traj')) {
        designsArray.push(designMap.get('traj')!);
      }
      
      console.log("Processed designs:", designsArray);
      setDesigns(designsArray);
    }
  }, [filesData]);
  
  // Function to check if a design is selected
  const isDesignSelected = (designFile: string) => {
    return selectedDesign === designFile;
  };
  
  // Function to create a link to view the structure
  const createStructureViewerLink = (designFile: string) => {
    return `#/jobs/${jobId}/rfdiffusion?tab=1&file=${designFile}`;
  };

  // Function to handle clicking on a design to view in 3D
  const handleViewStructure = (pdbFile: string) => {
    setSelectedDesign(pdbFile);
    // Find the tab=1 parameter in the URL which corresponds to the structure viewer tab
    window.location.href = createStructureViewerLink(pdbFile);
  };

  // Function to get file type icon
  const getFileIcon = (filename: string) => {
    if (filename.endsWith('/')) {
      return <Folder color="primary" />;
    } else if (filename.endsWith('.pdb')) {
      return <Description color="secondary" />;
    } else if (filename.endsWith('.trb')) {
      return <Science color="success" />;
    } else {
      return <Description />;
    }
  };
  
  // Function to get download URL for a file
  const getFileUrl = (filename: string) => {
    return `/api/rfdiffusion/jobs/${jobId}/files/${filename}`;
  };

  // Function to get file description
  const getFileDescription = (filename: string) => {
    if (filename.endsWith('.pdb')) {
      return 'Protein structure file (3D coordinates)';
    } else if (filename.endsWith('.trb')) {
      return 'Trajectory binary file (diffusion path)';
    } else if (filename.includes('_traj.pdb')) {
      if (filename.includes('Xt-1_traj')) {
        return 'Denoised structure trajectory';
      } else if (filename.includes('pX0_traj')) {
        return 'Initial noise trajectory';
      }
      return 'Trajectory PDB file';
    } else if (filename === 'traj/') {
      return 'Trajectory directory';
    }
    return '';
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        RFDiffusion Results
      </Typography>
      
      {/* Add a debug section during development */}
      {process.env.NODE_ENV === 'development' && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle2">Debug Info:</Typography>
          <Typography variant="body2">Files Loading: {filesLoading ? 'Yes' : 'No'}</Typography>
          <Typography variant="body2">Files Error: {filesError ? String(filesError) : 'None'}</Typography>
          <Typography variant="body2">Designs Count: {designs.length}</Typography>
          <Typography variant="body2">Files Count: {filesData?.files?.length || 0}</Typography>
        </Paper>
      )}
      
      {/* Design Parameters Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Design Parameters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Number of Designs</Typography>
            <Typography variant="body1" gutterBottom>{numDesigns}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Noise Scale</Typography>
            <Typography variant="body1" gutterBottom>{noiseScale}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Temperature</Typography>
            <Typography variant="body1" gutterBottom>{temperature}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Diffusion Steps</Typography>
            <Typography variant="body1" gutterBottom>{numSteps}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Random Seed</Typography>
            <Typography variant="body1" gutterBottom>{seed}</Typography>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Typography variant="subtitle2">Symmetry Type</Typography>
            <Typography variant="body1" gutterBottom>{symmetryType}</Typography>
          </Grid>
          {symmetryType !== 'None' && (
            <Grid item xs={12} md={6} lg={4}>
              <Typography variant="subtitle2">Symmetry Order</Typography>
              <Typography variant="body1" gutterBottom>{symmetryOrder}</Typography>
            </Grid>
          )}
          {contigs && contigs !== 'N/A' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">Contigs</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', overflowX: 'auto' }}>
                <Typography variant="body2" fontFamily="monospace" whiteSpace="pre-wrap">
                  {contigs}
                </Typography>
              </Paper>
            </Grid>
          )}
          {querySequence && (
            <Grid item xs={12}>
              <Typography variant="subtitle2">Query Sequence</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', overflowX: 'auto' }}>
                <Typography variant="body2" fontFamily="monospace" whiteSpace="pre-wrap">
                  {querySequence}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Design Results */}
      {designs.length > 0 ? (
        <Grid container spacing={3}>
          {/* Summary Table for designs */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Design Summary
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Design</TableCell>
                        <TableCell>Structure</TableCell>
                        <TableCell>Trajectories</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {designs.filter(d => d.designNumber !== 'traj').map((design) => {
                        const pdbFile = design.files.find(f => f.endsWith('.pdb') && !f.includes('_traj'));
                        const trajFiles = design.files.filter(f => f.includes('_traj.pdb'));
                        const isSelected = pdbFile ? isDesignSelected(pdbFile) : false;
                        
                        return (
                          <TableRow 
                            key={design.designNumber}
                            sx={{ bgcolor: isSelected ? 'action.selected' : 'inherit' }}
                          >
                            <TableCell component="th" scope="row">
                              Design {design.designNumber}
                            </TableCell>
                            <TableCell>
                              {pdbFile ? (
                                <Button 
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleViewStructure(pdbFile)}
                                >
                                  View Structure
                                </Button>
                              ) : 'No structure available'}
                            </TableCell>
                            <TableCell>
                              {trajFiles.length > 0 ? `${trajFiles.length} files` : 'None'}
                            </TableCell>
                            <TableCell align="right">
                              <Button 
                                size="small"
                                variant="outlined"
                                href={pdbFile ? getFileUrl(pdbFile) : '#'}
                                disabled={!pdbFile}
                                download
                              >
                                Download PDB
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {designs.map((design, index) => (
            <Grid item xs={12} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {design.designNumber === 'traj' ? 
                      'Trajectory Files' : 
                      `Design ${design.designNumber}`
                    }
                    {design.designNumber !== 'traj' && (
                      <Chip 
                        label={isDesignSelected(design.files.find(f => f.endsWith('.pdb') && !f.includes('_traj')) || '') ? "Currently Viewing" : "View in 3D"}
                        color={isDesignSelected(design.files.find(f => f.endsWith('.pdb') && !f.includes('_traj')) || '') ? "success" : "primary"}
                        size="small" 
                        onClick={() => {
                          const pdbFile = design.files.find(f => f.endsWith('.pdb') && !f.includes('_traj'));
                          if (pdbFile) handleViewStructure(pdbFile);
                        }}
                        clickable
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  
                  <List>
                    {design.files.map((file: string, fileIndex: number) => (
                      <ListItem key={fileIndex}>
                        <ListItemIcon>
                          {getFileIcon(file)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={file} 
                          secondary={getFileDescription(file)}
                        />
                        <Button 
                          variant="outlined" 
                          size="small"
                          href={getFileUrl(file)}
                          target="_blank"
                          download
                        >
                          Download
                        </Button>
                        {file.endsWith('.pdb') && !file.includes('_traj') && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewStructure(file)}
                            sx={{ ml: 1 }}
                          >
                            View 3D
                          </Button>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No design results available.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RFDiffusionResults; 